/**
 * Table utility functions and column configurations
 */
import React from 'react';
import { Button, Progress } from 'antd';
import {
    CheckCircleFilled,
    ExclamationCircleFilled,
    CloseCircleFilled,
    SyncOutlined,
    PlusSquareOutlined,
    MinusSquareOutlined,
} from '@ant-design/icons';
import { getStatusIcon, getStatusColor, getStatusText, getResourceName, getResourceType } from './preflightUtils';

/**
 * Render status icon based on icon configuration
 */
const renderStatusIcon = (iconConfig) => {
    if (!iconConfig) return null;

    const IconComponent = {
        CheckCircleFilled,
        ExclamationCircleFilled,
        CloseCircleFilled,
        SyncOutlined,
    }[iconConfig.type];

    if (!IconComponent) return null;

    return <IconComponent style={iconConfig.style} spin={iconConfig.spin} />;
};

/**
 * Create columns for preflight component table (main table with sources/targets)
 */
export const createPreflightComponentColumns = ({
    inspectSourcesMode,
    allComplete,
    expandedComponents,
    selectedRowKeys,
    onComponentExpand,
    onRowSelection,
    migrationContext = {},
}) => {
    const columns = [];

    // Expand button column - only show in migration readiness mode
    if (!inspectSourcesMode) {
        columns.push({
            key: 'expand',
            width: 40,
            render: (_, component) =>
                allComplete && component.data?.content ? (
                    <Button
                        type="text"
                        size="small"
                        icon={
                            expandedComponents.has(component.resource_id) ? (
                                <MinusSquareOutlined />
                            ) : (
                                <PlusSquareOutlined />
                            )
                        }
                        onClick={() => onComponentExpand(component.resource_id)}
                    />
                ) : null,
        });
    }

    // Checkbox column - only show in migration readiness mode
    if (!inspectSourcesMode) {
        columns.push({
            key: 'checkbox',
            width: 40,
            render: (_, component) => {
                const isNotTargetCloud = component.type !== 'target_cloud';
                const isSuccessOrWarning =
                    component.state === 'success' || (component.state === 'error' && component.errorType === 'warning');
                const isFailedButIgnored =
                    migrationContext?.ignoreValidationErrors &&
                    component.state === 'error' &&
                    component.errorType !== 'warning';

                const canSelect = allComplete && isNotTargetCloud && (isSuccessOrWarning || isFailedButIgnored);

                return canSelect ? (
                    <input
                        type="checkbox"
                        checked={selectedRowKeys.includes(component.resource_id)}
                        onChange={(e) => onRowSelection(component.resource_id, e.target.checked)}
                        data-resource-id={component.resource_id}
                    />
                ) : null;
            },
        });
    }

    // Name column
    columns.push({
        title: 'Name',
        key: 'name',
        width: 200,
        render: (_, component) => getResourceName(component, migrationContext),
    });

    // Type column - only show in migration readiness mode
    if (!inspectSourcesMode) {
        columns.push({
            title: 'Type',
            key: 'type',
            type: 200,
            render: (_, component) => getResourceType(component, migrationContext),
        });
    }

    // Status column
    columns.push({
        title: 'Status',
        key: 'status',
        width: 200,
        render: (_, component) => (
            <div>
                {component.state === 'pending' || component.state === 'running' ? (
                    <div style={{ display: 'flex', alignItems: 'center' }}>
                        <Progress
                            percent={component.progress}
                            size="small"
                            style={{ width: 100, marginRight: 8 }}
                            format={() => `${component.progress}%`}
                        />
                        {component.state === 'running' && <SyncOutlined spin />}
                    </div>
                ) : (
                    <div style={{ display: 'flex', alignItems: 'center' }}>
                        {renderStatusIcon(getStatusIcon(component.state, component.errorType))}
                        <span
                            style={{
                                marginLeft: 8,
                                fontWeight: 500,
                                color: getStatusColor(component.state, component.errorType),
                            }}
                        >
                            {getStatusText(component.state, component.errorType)}
                        </span>
                    </div>
                )}
            </div>
        ),
    });

    return columns;
};

/**
 * Create columns for preflight checks table (detailed checks within each component)
 */
export const createPreflightChecksColumns = ({ onRecoveryChange, componentId }) => [
    {
        title: 'Preflight Check',
        dataIndex: 'label',
        key: 'name',
        render: (text, record) => <strong>{text || record.name}</strong>,
    },
    {
        title: 'Description',
        dataIndex: 'description',
        key: 'description',
        render: (text, record) => (
            <div>
                {text}
                {/* Show errors if any */}
                {record.state === 'error' && record.errors && record.errors.length > 0 && (
                    <div style={{ marginTop: 8 }}>
                        <div style={{ color: '#ff4d4f', fontWeight: 500 }}>
                            {record.error_type === 'warning' ? 'Warning(s):' : 'Error(s):'}
                        </div>
                        {record.errors.map((error, index) => (
                            <div key={index}>
                                <div style={{ display: 'flex', alignItems: 'flex-start', marginTop: 4 }}>
                                    <ExclamationCircleFilled
                                        style={{
                                            color: record.error_type === 'warning' ? '#faad14' : '#ff4d4f',
                                            marginRight: 8,
                                            marginTop: 2,
                                        }}
                                    />
                                    <div style={{ fontSize: '12px' }}>
                                        {error.message}
                                        {error.resolution && (
                                            <div style={{ marginTop: 2 }}>
                                                <strong>Resolution: </strong>
                                                {error.resolution}
                                            </div>
                                        )}
                                    </div>
                                </div>
                                {/* Recovery UI will be rendered by parent component */}
                                {error.recovery && onRecoveryChange && (
                                    <div id={`recovery-${componentId}-${record.id}-${index}`} />
                                )}
                            </div>
                        ))}
                    </div>
                )}
            </div>
        ),
    },
    {
        title: 'Status',
        key: 'status',
        width: 80,
        render: (_, record) => {
            const status = record.state || 'pending';
            const errorType = record.error_type;
            return (
                <div style={{ textAlign: 'center' }}>
                    {status === 'running' ? (
                        <SyncOutlined spin style={{ color: '#1890ff' }} />
                    ) : (
                        renderStatusIcon(getStatusIcon(status, errorType))
                    )}
                </div>
            );
        },
    },
];

/**
 * Create columns for header table (expand all, select all controls)
 */
export const createHeaderTableColumns = ({
    inspectSourcesMode,
    allComplete,
    expandedComponents,
    preflightComponents,
    selectAll,
    onExpandAll,
    onSelectAll,
}) => {
    const columns = [];

    // Expand All button - only show in migration readiness mode
    if (!inspectSourcesMode) {
        columns.push({
            key: 'expandAll',
            width: 40,
            render: () => (
                <Button
                    type="text"
                    size="small"
                    icon={
                        expandedComponents.size === preflightComponents.length ? (
                            <MinusSquareOutlined />
                        ) : (
                            <PlusSquareOutlined />
                        )
                    }
                    onClick={onExpandAll}
                    title="Expand/Collapse All"
                />
            ),
        });
    }

    // Select All checkbox - only show in migration readiness mode
    if (!inspectSourcesMode) {
        columns.push({
            key: 'selectAll',
            width: 40,
            render: () =>
                allComplete ? (
                    <input
                        type="checkbox"
                        checked={selectAll}
                        onChange={(e) => onSelectAll(e.target.checked)}
                        title="Select/Deselect All"
                    />
                ) : null,
        });
    }

    // Header labels
    columns.push({
        key: 'name',
        width: 250,
        render: () => <strong>Name</strong>,
    });

    if (!inspectSourcesMode) {
        columns.push({
            key: 'type',
            width: 200,
            render: () => <strong>Type</strong>,
        });
    }

    columns.push({
        key: 'status',
        align: 'center',
        render: () => <strong>Inspection Status</strong>,
    });

    return columns;
};
